import { useEffect, useState } from "react";
import { IChildForm } from "../../../";
import { useIntl } from "react-intl";
import Checkbox from "@rio-cloud/rio-uikit/Checkbox";
import { formatPhoneNumber } from "../../../../../utils/phoneUtils";
import { isMobile } from "@rio-cloud/rio-uikit/utils/deviceUtils";
import { OccurrenceTypeEnum } from "../../../../../enums/occurrenceType";
import { isCancelledOccurrenceDispatch } from "../../../../../hooks/useDispatch";

type ContactGroup = {
    prefix: "requester" | "onSitePerson";
    nameKey: string;
    roleKey: string;
    phoneKey: string;
};

const CONTACT_GROUPS: ContactGroup[] = [
    { prefix: "requester", nameKey: "requesterName", roleKey: "requesterRole", phoneKey: "requesterPhone" },
    { prefix: "onSitePerson", nameKey: "onSitePersonName", roleKey: "onSitePersonRole", phoneKey: "onSitePersonPhone" },
];

const DRIVER_ROLE = "Motorista";

const TowTruckContactsChildForm = (props: IChildForm) => {
    const intl = useIntl();

    const driverName = props.occurrence?.driverName ?? '';
    const driverPhone = props.occurrence?.driverPhone ?? '';
    const [sameAsDriver, setSameAsDriver] = useState(() =>
        !!props.occurrence?.onSitePersonName
        && props.occurrence?.onSitePersonName === driverName
        && props.occurrence?.onSitePersonPhone === driverPhone
        && props.occurrence?.onSitePersonRole === DRIVER_ROLE
    );

    // Enquanto marcado, a pessoa no local acompanha os dados do motorista
    useEffect(() => {
        if (!sameAsDriver || !props.occurrence) return;
        if (props.occurrence.onSitePersonName !== driverName) props.setOccurrenceValue('onSitePersonName', driverName);
        if (props.occurrence.onSitePersonRole !== DRIVER_ROLE) props.setOccurrenceValue('onSitePersonRole', DRIVER_ROLE);
        if (props.occurrence.onSitePersonPhone !== driverPhone) props.setOccurrenceValue('onSitePersonPhone', driverPhone);
    }, [sameAsDriver, driverName, driverPhone, props.occurrence?.onSitePersonName, props.occurrence?.onSitePersonRole, props.occurrence?.onSitePersonPhone]);

    const handleSameAsDriverChange = (checked: boolean) => {
        setSameAsDriver(checked);
        if (!checked) {
            props.setOccurrenceValue('onSitePersonName', '');
            props.setOccurrenceValue('onSitePersonRole', '');
            props.setOccurrenceValue('onSitePersonPhone', '');
        }
    };

    const isTowTruck = props.occurrence?.occurrenceType === OccurrenceTypeEnum.WINCH;
    const hasActiveDispatch = (props.occurrence?.dispatches ?? []).some(dispatch => !isCancelledOccurrenceDispatch(dispatch));

    if (!isTowTruck || !hasActiveDispatch) return null;

    const fieldClassName = (key: string) =>
        `form-group form-group-lg w-full ${props.hasError && props.requiredFields.includes(key) && !(props.occurrence as any)?.[key] ? 'has-error' : ''}`;

    const renderField = (key: string, labelId: string, maxLength: number, isPhone = false, forceDisabled = false) => {
        if (props.hiddenFields.includes(key)) return null;
        const label = intl.formatMessage({ id: labelId });
        return (
            <div className={fieldClassName(key)}>
                <label htmlFor={key} className="truncate">{label}{props.requiredFields.includes(key) ? ' *' : ''}</label>
                <input
                    type="text"
                    className="form-control"
                    id={key}
                    placeholder={label}
                    disabled={forceDisabled || props.disabledAllFields || props.disabledFields.includes(key)}
                    value={(props.occurrence as any)?.[key] ?? ''}
                    onChange={(e) => props.occurrence && props.setOccurrenceValue(key, isPhone ? formatPhoneNumber(e.target.value) : e.target.value)}
                    maxLength={maxLength}
                />
            </div>
        );
    };

    return (
        <>
            {CONTACT_GROUPS.map(group => {
                const isOnSite = group.prefix === "onSitePerson";
                const lockFields = isOnSite && sameAsDriver;
                return (
                <div key={group.prefix} className="w-full my-6 p-6 pb-0 border rounded">
                    <div className={`flex justify-between w-full gap-6 ${isMobile() ? 'flex-wrap' : ''}`}>
                        {renderField(group.nameKey, `intl-msg:kanban.form.towTruckContactsChildForm.${group.prefix}.name`, 100, false, lockFields)}
                        {renderField(group.roleKey, `intl-msg:kanban.form.towTruckContactsChildForm.${group.prefix}.role`, 100, false, lockFields)}
                        {renderField(group.phoneKey, `intl-msg:kanban.form.towTruckContactsChildForm.${group.prefix}.phone`, 15, true, lockFields)}
                    </div>
                    {isOnSite && (
                        <div className="mb-4">
                            <Checkbox
                                checked={sameAsDriver}
                                disabled={props.disabledAllFields}
                                onChange={(e) => handleSameAsDriverChange(e.target.checked)}
                            >
                                {intl.formatMessage({ id: "intl-msg:kanban.form.towTruckContactsChildForm.sameAsDriver" })}
                            </Checkbox>
                        </div>
                    )}
                </div>
                );
            })}
        </>
    );
}

export default TowTruckContactsChildForm;
